/** UTC-in, user-timezone-out presentation. Never changes event ordering or deadlines. */
export const TIMEZONE_CHANGED_EVENT = "cmc:timezone-changed";

const FALLBACK_ZONES = [
  "UTC",
  "Asia/Kuala_Lumpur",
  "Asia/Singapore",
  "Asia/Hong_Kong",
  "Asia/Tokyo",
  "Australia/Sydney",
  "Europe/London",
  "Europe/Paris",
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
];
const CURRENT_ZONE_KEY = "cmc.timezone.current";

function storageKey(username) {
  return "cmc.timezone." + String(username || "anonymous").trim().toLowerCase();
}

export function isIanaTimezone(value) {
  const zone = String(value || "").trim();
  if (!zone) return false;
  try {
    new Intl.DateTimeFormat("en", { timeZone: zone }).format(0);
    return true;
  } catch {
    return false;
  }
}

export function supportedTimezones() {
  try {
    const zones = Intl.supportedValuesOf("timeZone");
    return ["UTC", ...zones.filter((zone) => zone !== "UTC")];
  } catch {
    return FALLBACK_ZONES;
  }
}

export function timezoneOf(me) {
  const username = me && me.username;
  try {
    const local = localStorage.getItem(storageKey(username));
    if (isIanaTimezone(local)) return local;
    if (!username) {
      const current = localStorage.getItem(CURRENT_ZONE_KEY);
      if (isIanaTimezone(current)) return current;
    }
  } catch { /* storage may be unavailable */ }
  const server = me && (me.timezone || (me.raw && me.raw.timezone));
  if (isIanaTimezone(server)) return String(server);
  try {
    const browser = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (isIanaTimezone(browser)) return browser;
  } catch { /* use UTC */ }
  return "UTC";
}

export function setTimezonePreference(username, value) {
  const zone = String(value || "").trim();
  if (!isIanaTimezone(zone)) throw new Error("invalid_iana_timezone");
  localStorage.setItem(storageKey(username), zone);
  localStorage.setItem(CURRENT_ZONE_KEY, zone);
  dispatchEvent(new CustomEvent(TIMEZONE_CHANGED_EVENT, { detail: { username, zone } }));
  return zone;
}

export function activateTimezone(me) {
  const zone = timezoneOf(me);
  try {
    localStorage.setItem(CURRENT_ZONE_KEY, zone);
  } catch { /* storage may be unavailable */ }
  dispatchEvent(new CustomEvent(TIMEZONE_CHANGED_EVENT, {
    detail: { username: me && me.username, zone },
  }));
  return zone;
}

export function instantMs(value) {
  if (value instanceof Date) return value.getTime();
  if (typeof value === "number" && Number.isFinite(value)) {
    return Math.abs(value) < 10_000_000_000 ? value * 1000 : value;
  }
  const text = String(value == null ? "" : value).trim();
  if (!text) return Number.NaN;
  const utc = /^\d{4}-\d\d-\d\dT/.test(text) && !/(?:Z|[+-]\d\d:\d\d)$/.test(text)
    ? text + "Z"
    : text;
  return Date.parse(utc);
}

export function formatInstant(value, options = {}) {
  const date = new Date(instantMs(value));
  if (!Number.isFinite(date.getTime())) return "—";
  const zone = isIanaTimezone(options.timeZone) ? options.timeZone : "UTC";
  return new Intl.DateTimeFormat(options.locale || undefined, {
    timeZone: zone,
    year: options.date === false ? undefined : "numeric",
    month: options.date === false ? undefined : "2-digit",
    day: options.date === false ? undefined : "2-digit",
    hour: options.dateOnly ? undefined : "2-digit",
    minute: options.dateOnly ? undefined : "2-digit",
    second: options.dateOnly || options.seconds === false ? undefined : "2-digit",
    hourCycle: "h23",
    timeZoneName: options.zoneName ? "short" : undefined,
  }).format(date);
}

export function formatUserInstant(value, options = {}) {
  return formatInstant(value, { ...options, timeZone: timezoneOf() });
}
